import { Check, LockKeyhole, X } from "@tamagui/lucide-icons";
import React, { useContext, useEffect } from "react";
import { useReducedMotion } from "react-native-reanimated";
import {
  Circle,
  type FontSizeTokens,
  type GetProps,
  XStack,
  YStack,
  withStaticProperties,
} from "tamagui";

import { Ul } from "./Layout";
import { LayoutContext } from "./LayoutProvider";
import { Paragraph } from "./Typography";

type ItemText = string | { heading: string; subtext?: string };

interface BulletItemProps
  extends Omit<GetProps<typeof XStack>, "color" | "size"> {
  itemText: ItemText;
  renderText?: (text: string) => React.ReactNode;
  icon?: React.ReactNode;
  size?: FontSizeTokens;
  color?: string;
  iconContainerProps?: GetProps<typeof XStack>;
}

export const ListCheck: React.FC<GetProps<typeof Circle>> = (props) => (
  <Circle backgroundColor="$textSecondary" size="$100" {...props}>
    <Check flexShrink={0} size="$200" color="$background" />
  </Circle>
);
export const ListBullet: React.FC<GetProps<typeof Circle>> = (props) => (
  <Circle
    backgroundColor="$textSecondary"
    size="$50"
    marginVertical="$125"
    {...props}
  />
);
export const ListX: React.FC<GetProps<typeof Circle>> = (props) => (
  <Circle backgroundColor="$red" size="$100" {...props}>
    <X flexShrink={0} size="$200" color="$textSecondary" />
  </Circle>
);
export const ListLock: React.FC<GetProps<typeof LockKeyhole>> = (props) => (
  <LockKeyhole size="$100" color="$textSecondary" {...props} />
);

export const BulletDoubleItem = ({
  itemText,
  icon,
  size,
  color,
  renderText,
  ...props
}: BulletItemProps) => (
  <XStack
    alignItems="center"
    gap="$200"
    key={typeof itemText === "string" ? itemText : itemText.heading}
    {...props}
  >
    {icon}
    <YStack gap="$50" flexShrink={1}>
      <Paragraph size={size} color={color} bold>
        {typeof itemText === "string"
          ? (renderText?.(itemText) ?? itemText)
          : itemText.heading}
      </Paragraph>
      <Paragraph size={size} color={color}>
        {typeof itemText === "string" ? "" : itemText.subtext}
      </Paragraph>
    </YStack>
  </XStack>
);
export const BulletItem = ({
  itemText,
  icon,
  size = "$1000" as FontSizeTokens,
  iconContainerProps,
  color,
  renderText,
  ...props
}: BulletItemProps) => (
  <XStack
    role="listitem"
    alignItems="flex-start"
    gap="$100"
    key={typeof itemText === "string" ? itemText : itemText.heading || "item"}
    {...props}
  >
    <XStack
      flexShrink={0}
      width={icon != null ? "$300" : undefined}
      alignItems="center"
      justifyContent="center"
      {...iconContainerProps}
    >
      {icon}
    </XStack>
    <Paragraph size={size} alignSelf="center" flexShrink={1} color={color}>
      {typeof itemText === "string" && renderText
        ? renderText(itemText)
        : itemText}
    </Paragraph>
  </XStack>
);

interface BulletedListProps extends Omit<BulletItemProps, "itemText"> {
  items: ItemText[];
  separator?: React.ReactNode;
  rootProps?: GetProps<typeof Ul>;
}

const BulletedList = ({
  items,
  separator = null,
  rootProps,
  ...props
}: BulletedListProps) => (
  <Ul {...rootProps}>
    {items.map((item, index) => (
      <React.Fragment key={typeof item === "string" ? item : item.heading}>
        {index !== 0 && separator}
        {typeof item !== "string" && item.heading ? (
          <BulletDoubleItem itemText={item} {...props} />
        ) : (
          <BulletItem itemText={item} {...props} />
        )}
      </React.Fragment>
    ))}
  </Ul>
);

BulletedList.displayName = "BulletedList";

const AnimatedBulletedList = ({
  id,
  items,
  separator = null,
  icon,
  doneIcon,
  delay = 5000,
  rootProps,
  ...props
}: {
  id: string;
  doneIcon?: React.ReactNode;
  delay?: number;
} & BulletedListProps) => {
  const reducedMotion = useReducedMotion();
  const { incrementSharedLoadingStateCount, getSharedLoadingStateCount } =
    useContext(LayoutContext);
  const doneCount = reducedMotion ? 0 : getSharedLoadingStateCount(id);

  useEffect(() => {
    if (reducedMotion) return;
    if (doneCount < items.length - 1) {
      const timer = setTimeout(() => {
        incrementSharedLoadingStateCount(id);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [
    doneCount,
    items.length,
    delay,
    id,
    incrementSharedLoadingStateCount,
    reducedMotion,
  ]);

  return (
    <Ul {...rootProps}>
      {items.map((item, index) => (
        <React.Fragment key={typeof item === "string" ? item : item.heading}>
          {index !== 0 && separator}
          {typeof item !== "string" && item.heading ? (
            <BulletDoubleItem
              itemText={item}
              icon={index < doneCount ? doneIcon : icon}
              {...props}
            />
          ) : (
            <BulletItem
              itemText={item}
              icon={index < doneCount ? doneIcon : icon}
              {...props}
            />
          )}
        </React.Fragment>
      ))}
    </Ul>
  );
};

export default withStaticProperties(BulletedList, {
  Animated: AnimatedBulletedList,
  Check: ListCheck,
  Bullet: ListBullet,
  X: ListX,
  Lock: ListLock,
});
